import React, { useState, useEffect } from 'react';
import { MessageCircle, Search, Heart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Conversations = () => {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const pollingIntervalRef = React.useRef(null);

  useEffect(() => {
    loadConversations();
    
    // Start polling for conversation updates every 5 seconds
    startPolling();

    return () => {
      // Clean up polling interval
      stopPolling();
    };
  }, []);

  // Pause/resume polling based on page visibility
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.log('🔕 Page hidden - pausing conversation polling');
        stopPolling();
      } else {
        console.log('🔔 Page visible - resuming conversation polling');
        startPolling();
        // Immediately fetch conversations when tab becomes visible
        loadConversations();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const startPolling = () => {
    // Clear any existing interval first
    stopPolling();
    
    console.log('✅ Starting conversation polling (every 5 seconds)');
    pollingIntervalRef.current = setInterval(() => {
      console.log('🔄 Polling for conversation updates...');
      loadConversations();
    }, 5000); // Poll every 5 seconds
  };

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      console.log('⏸️ Stopping conversation polling');
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  const loadConversations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat/conversations`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setConversations(data.conversations || []);
      }
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const openChat = (conversationId) => {
    navigate(`/chat/${conversationId}`);
  };

  const filteredConversations = conversations.filter(conv =>
    conv.participant.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatLastMessageTime = (timestamp) => {
    if (!timestamp) return '';
    
    const now = new Date();
    const messageTime = new Date(timestamp);
    const diffMs = now - messageTime;
    const diffHours = diffMs / (1000 * 60 * 60);
    const diffDays = diffMs / (1000 * 60 * 60 * 24);

    if (diffHours < 1) {
      const diffMins = Math.floor(diffMs / (1000 * 60));
      return diffMins < 1 ? 'now' : `${diffMins}m`;
    } else if (diffHours < 24) {
      return `${Math.floor(diffHours)}h`;
    } else if (diffDays < 7) {
      return `${Math.floor(diffDays)}d`;
    } else {
      return messageTime.toLocaleDateString();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-400 via-pink-500 to-purple-600 flex items-center justify-center overflow-x-hidden">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-400 via-pink-500 to-purple-600">
      {/* Header */}
      <div className="bg-white/10 backdrop-blur-md border-b border-white/20 p-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-3xl font-bold text-white">Messages</h1>
            <button
              onClick={() => navigate('/matches')}
              className="bg-white/20 text-white px-4 py-2 rounded-full hover:bg-white/30 transition-colors flex items-center space-x-2"
            >
              <Heart className="h-4 w-4" />
              <span>Find Matches</span>
            </button>
          </div>
          
          {/* Search Bar */}
          <div className="relative">
            <Search className="h-5 w-5 text-white/60 absolute left-3 top-1/2 transform -translate-y-1/2" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search conversations..."
              className="w-full bg-white/10 border border-white/20 rounded-full pl-10 pr-4 py-2 text-white placeholder-white/60 focus:outline-none focus:border-white/40"
            />
          </div>
        </div>
      </div>

      {/* Conversations List */}
      <div className="max-w-4xl mx-auto p-4">
        {filteredConversations.length === 0 ? (
          <div className="text-center py-12">
            <MessageCircle className="h-16 w-16 text-white/50 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">
              {conversations.length === 0 ? 'No Messages Yet' : 'No matching conversations'}
            </h3>
            <p className="text-white/80 mb-6">
              {conversations.length === 0 
                ? 'Start matching with people to begin conversations!' 
                : 'Try a different search term'
              }
            </p>
            {conversations.length === 0 && (
              <button
                onClick={() => navigate('/matches')}
                className="bg-white text-rose-500 px-6 py-3 rounded-full font-semibold hover:bg-rose-50 transition-colors"
              >
                Find Matches
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredConversations.map((conversation) => (
              <div
                key={conversation.conversation_id}
                className="bg-white/10 backdrop-blur-md rounded-2xl p-4 hover:bg-white/15 transition-colors cursor-pointer"
                onClick={() => openChat(conversation.conversation_id)}
              >
                <div className="flex items-center space-x-4">
                  {/* Profile Photo */}
                  <div className="relative">
                    {conversation.participant.photo_url ? (
                      <img
                        src={conversation.participant.photo_url}
                        alt={conversation.participant.name}
                        className="w-16 h-16 rounded-full object-cover border-2 border-white/20"
                      />
                    ) : (
                      <div className="w-16 h-16 bg-gradient-to-br from-rose-400 to-pink-600 rounded-full flex items-center justify-center">
                        <span className="text-white text-xl font-bold">
                          {conversation.participant.name.charAt(0)}
                        </span>
                      </div>
                    )}
                    {conversation.unread_count > 0 && (
                      <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-6 w-6 flex items-center justify-center font-bold">
                        {conversation.unread_count > 9 ? '9+' : conversation.unread_count}
                      </div>
                    )}
                  </div>

                  {/* Conversation Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-semibold text-white truncate">
                        {conversation.participant.name}
                      </h3>
                      <span className="text-xs text-white/60">
                        {formatLastMessageTime(conversation.last_message_at)}
                      </span>
                    </div>
                    
                    <p className="text-sm text-white/70 truncate">
                      {conversation.last_message || 'Start your conversation...'}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Conversations;